export const clientTerms = {
    // ✅ UPDATED: Changed from "Client Terms of Use" to something more appropriate
    title: "Terms and Conditions",
    lastUpdated: "January 15, 2025",
    effectiveDate: "January 15, 2025",
    
    introduction: `These Terms of Use ("Terms") govern your interaction with the Inked Up Manager platform ("Platform") as a client receiving services from tattoo artists or shops who use our software. By using any digital forms, booking systems, or other features provided through this Platform, you agree to these Terms.`,
    
    importantNotice: `Inked Up Manager is a software service provider only. We do not provide tattoo services and are not responsible for the quality, safety, or outcomes of any tattoo or body art services you receive.`,
    
    // ... rest of your sections stay exactly the same
    sections: [
      {
        id: "platform-overview",
        title: "PLATFORM OVERVIEW",
        subsections: [
          {
            id: "our-role",
            title: "Our Role",
            content: "Inked Up Manager provides software tools that enable tattoo artists and shops to:",
            list: [
              "Manage client information and appointments",
              "Collect digital consent forms and waivers", 
              "Process payments and communications",
              "Store client records and documentation"
            ]
          },
          {
            id: "not-service-provider",
            title: "We Are Not Your Service Provider",
            content: "Important Distinctions:",
            list: [
              "We do not provide tattoo, piercing, or any body art services",
              "We do not employ, certify, or supervise tattoo artists",
              "We are not affiliated with or responsible for any individual artist or shop",
              "Your contractual relationship is with the artist/shop, not with us"
            ]
          }
        ]
      },
      
      {
        id: "no-liability",
        title: "NO LIABILITY FOR TATTOO SERVICES",
        subsections: [
          {
            id: "complete-disclaimer",
            title: "Complete Disclaimer",
            content: "YOU EXPRESSLY UNDERSTAND AND AGREE THAT INKED UP MANAGER:",
            list: [
              "Has no liability for the quality, safety, results, or outcomes of any tattoo or body art service",
              "Does not vet, verify, certify, or guarantee the skills, qualifications, or credentials of any artist or shop",
              "Is not responsible for artist conduct, professionalism, or adherence to health standards",
              "Cannot and does not guarantee compliance with health regulations by artists or shops"
            ]
          },
          {
            id: "your-responsibility",
            title: "Your Responsibility",
            content: "You are solely responsible for:",
            list: [
              "Researching and selecting qualified, licensed professionals",
              "Verifying artist credentials and shop licenses",
              "Ensuring the facility meets health and safety standards",
              "Making informed decisions about your body art choices"
            ]
          },
          {
            id: "health-safety",
            title: "Health and Safety",
            content: "Critical Reminders:",
            list: [
              "Verify that your chosen artist/shop holds current licenses and permits",
              "Ensure all equipment is properly sterilized and safety protocols are followed",
              "Discuss any health conditions, allergies, or medications with your artist",
              "Follow all aftercare instructions provided by your artist"
            ]
          }
        ]
      },
  
      {
        id: "consent-forms",
        title: "CONSENT FORMS AND DOCUMENTATION",
        subsections: [
          {
            id: "digital-consent",
            title: "Digital Consent Process",
            content: "When you complete digital forms through our Platform:",
            list: [
              "Your responses are securely transmitted to your chosen artist/shop",
              "The artist/shop remains responsible for obtaining proper legal consent",
              "Digital signatures have the same legal effect as handwritten signatures"
            ]
          },
          {
            id: "form-validity",
            title: "Form Validity",
            list: [
              "Consent forms are created by the artist/shop, not by Inked Up Manager",
              "We provide the technology platform but do not review form content for legal compliance",
              "The artist/shop is responsible for ensuring forms meet local legal requirements"
            ]
          },
          {
            id: "rights-responsibilities",
            title: "Your Rights and Responsibilities",
            list: [
              "Read all forms carefully before signing",
              "Ask questions if anything is unclear",
              "Ensure all information you provide is accurate and complete",
              "Understand that consent forms are legally binding documents"
            ]
          }
        ]
      },
  
      {
        id: "privacy-data",
        title: "PRIVACY AND DATA HANDLING",
        subsections: [
          {
            id: "information-collection",
            title: "Information Collection",
            content: "When you use our Platform, information may be collected including:",
            list: [
              "Personal contact information (name, email, phone)",
              "Health-related information relevant to your service",
              "Photos or documentation related to your body art",
              "Payment and booking information"
            ]
          },
          {
            id: "data-sharing",
            title: "Data Sharing",
            content: [
              "Your information is shared with:",
              "• The specific artist/shop providing your services",
              "• Payment processors for transaction completion", 
              "• Other service providers necessary to operate the Platform (under strict confidentiality)",
              "",
              "We DO NOT:",
              "• Sell your personal information to third parties",
              "• Use your data for marketing unrelated to your chosen artist/shop",
              "• Share your information with other artists/shops without your consent"
            ]
          },
          {
            id: "data-retention",
            title: "Data Retention",
            list: [
              "Your data is stored securely while you remain an active client",
              "Artists/shops control their own data retention policies",
              "You may request data deletion subject to legal and business requirements"
            ]
          }
        ]
      },
  
      {
        id: "payment-booking",
        title: "PAYMENT AND BOOKING",
        subsections: [
          {
            id: "payment-processing",
            title: "Payment Processing",
            list: [
              "Payments are processed through secure third-party providers",
              "We may facilitate payments but are not responsible for billing disputes",
              "Refund policies are set by individual artists/shops, not by Inked Up Manager"
            ]
          },
          {
            id: "booking-appointments",
            title: "Booking and Appointments",
            list: [
              "Appointment scheduling is managed by the artist/shop",
              "We provide the booking platform but are not responsible for scheduling conflicts",
              "Cancellation and rescheduling policies are set by the artist/shop"
            ]
          }
        ]
      },
  
      {
        id: "disputes-complaints",
        title: "DISPUTES AND COMPLAINTS",
        subsections: [
          {
            id: "direct-resolution",
            title: "Direct Resolution Required",
            content: "ALL disputes, complaints, or issues related to your tattoo services must be addressed directly with your artist/shop, including but not limited to:",
            list: [
              "Service quality or results",
              "Pricing or billing disputes",
              "Scheduling conflicts or cancellations",
              "Health or safety concerns",
              "Professional conduct issues",
              "Aftercare or healing problems"
            ]
          },
          {
            id: "platform-issues",
            title: "Platform Issues Only",
            content: "Contact Inked Up Manager only for:",
            list: [
              "Technical problems with the Platform",
              "Privacy or data security concerns",
              "Billing issues related to platform fees (if any)"
            ]
          },
          {
            id: "no-mediation",
            title: "No Mediation Services",
            content: "We do not mediate disputes between clients and artists/shops and cannot provide refunds for services rendered by third parties."
          }
        ]
      },
  
      {
        id: "limitation-liability",
        title: "LIMITATION OF LIABILITY",
        subsections: [
          {
            id: "platform-liability",
            title: "Platform Liability Only",
            content: "Our liability is limited strictly to the operation of the software Platform. We are not liable for:",
            list: [
              "Any aspect of tattoo or body art services",
              "Artist or shop conduct or professionalism",
              "Health outcomes or complications",
              "Financial losses related to your services",
              "Appointment scheduling or coordination issues"
            ]
          },
          {
            id: "maximum-liability",
            title: "Maximum Liability",
            content: "Our total liability to you for any Platform-related issues shall not exceed $100 or the amount of any Platform fees you have paid, whichever is greater."
          },
          {
            id: "warranty-disclaimer",
            title: "Warranty Disclaimer",
            content: "THE PLATFORM IS PROVIDED \"AS IS\" WITHOUT WARRANTIES. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE."
          }
        ]
      },
  
      {
        id: "indemnification",
        title: "INDEMNIFICATION",
        content: "You agree to indemnify and hold harmless Inked Up Manager from any claims, damages, or expenses arising from:",
        list: [
          "Your interactions with artists or shops",
          "Any tattoo or body art services you receive",
          "Your violation of these Terms",
          "Your use of the Platform"
        ]
      },
  
      {
        id: "emergency-situations",
        title: "EMERGENCY SITUATIONS",
        subsections: [
          {
            id: "medical-emergencies",
            title: "Medical Emergencies",
            content: "In case of medical emergency:",
            list: [
              "Seek immediate medical attention (call 911)",
              "Contact your tattoo artist/shop",
              "Contact your healthcare provider"
            ]
          },
          {
            id: "platform-emergencies",
            title: "Platform Emergencies",
            content: [
              "For urgent Platform-related issues (data breaches, security concerns):",
              "Email: security@inkedupmanager.com",
              "Our emergency response team will address security issues within 24 hours"
            ]
          }
        ]
      },
  
      {
        id: "minors-consent",
        title: "MINORS AND CONSENT",
        subsections: [
          {
            id: "age-restrictions",
            title: "Age Restrictions",
            list: [
              "Tattoo services are typically restricted to adults (18+) or minors with parental consent",
              "Age requirements are determined by local law and artist/shop policies",
              "Inked Up Manager does not verify age or parental consent"
            ]
          },
          {
            id: "parental-responsibility",
            title: "Parental Responsibility",
            content: "Parents/guardians are responsible for:",
            list: [
              "Ensuring compliance with local age requirements",
              "Providing proper consent for minor children",
              "Supervising minor children during the consent process"
            ]
          }
        ]
      },
  
      {
        id: "international-users",
        title: "INTERNATIONAL USERS",
        subsections: [
          {
            id: "local-laws",
            title: "Local Laws Apply",
            list: [
              "Tattoo regulations vary significantly by location",
              "You are responsible for ensuring compliance with local laws",
              "Some Platform features may not be available in all jurisdictions"
            ]
          },
          {
            id: "data-transfer",
            title: "Data Transfer",
            content: "By using the Platform, you consent to the transfer of your data to the United States where our servers are located."
          }
        ]
      },
  
      {
        id: "changes-terms",
        title: "CHANGES TO TERMS",
        subsections: [
          {
            id: "updates",
            title: "Updates",
            content: "We may update these Terms periodically. Continued use of the Platform after changes constitutes acceptance of new Terms."
          },
          {
            id: "notification",
            title: "Notification",
            content: "We will provide reasonable notice of material changes through:",
            list: [
              "Email notifications",
              "Platform announcements",
              "Updates to this document with revision dates"
            ]
          }
        ]
      },
  
      {
        id: "governing-law",
        title: "GOVERNING LAW",
        content: "These Terms are governed by the laws of [State/Jurisdiction]. Any disputes shall be resolved in the courts of [Jurisdiction] or through binding arbitration as we elect."
      },
  
      {
        id: "contact-information",
        title: "CONTACT INFORMATION",
        subsections: [
          {
            id: "customer-support",
            title: "Customer Support",
            content: [
              "For Platform-related issues:",
              "Email: support@inkedupmanager.com",
              //"Phone: [Support Phone Number]",
              "Business Hours: Monday-Friday, 9 AM - 5 PM EST"
            ]
          },
          {
            id: "privacy-concerns",
            title: "Privacy and Data Concerns",
            content: [
              "For privacy-related questions:",
              "Email: privacy@inkedupmanager.com",
              "Address: 1250 Scenic Highway S STE 1701 #206, Lawrenceville, GA 30045"
            ]
          },
          {
            id: "legal-notices",
            title: "Legal Notices",
            content: [
              "For legal matters:",
              "Email: legal@inkedupmanager.com",
              "Address: 1250 Scenic Highway S STE 1701 #206, Lawrenceville, GA 30045"
            ]
          }
        ]
      },
  
      {
        id: "acknowledgment",
        title: "ACKNOWLEDGMENT",
        content: "By using the Inked Up Manager Platform, you acknowledge that:",
        list: [
          "You have read and understood these Terms",
          "You understand the distinction between Inked Up Manager and your service provider",
          "You accept full responsibility for choosing qualified professionals",
          "You agree to resolve service-related disputes directly with your artist/shop",
          "You will not hold Inked Up Manager liable for tattoo services or outcomes"
        ]
      }
    ]
  };